import { addDays, endOfDay } from "date-fns";

import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";

export const useCoursePageData = async (
  slug: string,
  options?: { registration_token: string; from_project?: boolean },
) => {
  const { t } = useI18n();
  const courseCache = useCourseCache();
  const cachedData = courseCache.getBySlug(slug);

  const {
    data: course,
    refresh,
    status,
  } = await useFetch(`/api/courses/${slug}`, {
    query: {
      registration_token: options?.registration_token,
      from_project: options?.from_project,
    },
    deep: false,
    /*
        Lazy behaviour only if course has been previously cached from course any card list
        If not, course page view request is executed before route navigation and we can ensure
        that we don't throw 404 error in case of empty cache but existing course
       */
    lazy: !!cachedData,
    default: () => cachedData,
    transform(data) {
      if (!data) return null;

      const ratings = data.ratings[0];

      return {
        ...data,
        ratings:
          ratings.avg !== null && ratings.count > 0
            ? {
                avg: ratings.avg,
                count: ratings.count,
              }
            : null,
        is_masterclass: data.content_type === "MASTERCLASS",
        from_project: options?.from_project,
      } as CoursePageView;
    },
  });

  if (!course.value) {
    throw createError({
      fatal: true,
      statusCode: 404,
      statusMessage: t("app.errors.page_not_found"),
    });
  }

  courseCache.setMultiple([course.value]);

  const sessionViews = computed<CourseSessionView[]>(() => {
    return (
      course.value?.sessions.map((session) => {
        return {
          ...session,
          registrationLimitDate: endOfDay(
            addDays(new Date(session.start_date!), -1),
          ).toISOString(),
          enrolRoute: {
            name: "courses-slug-sessions-sessionId-enrol-index",
            params: {
              slug: course.value?.slug as string,
              sessionId: session.id,
            },
          },
        };
      }) ?? []
    );
  });

  const nextSession = computed(() => {
    return sessionViews.value.find(
      (session) =>
        (session.public || options?.registration_token) && !session.is_sold_out,
    );
  });

  const otherSessions = computed(() =>
    sessionViews.value.filter(
      (session) => session.id !== nextSession.value?.id,
    ),
  );

  if (import.meta.client) {
    const { $on } = useNuxtApp();
    $on("registration_request_created", refresh);
  }

  const isRegistrationLink = computed(() => {
    return !!options?.registration_token;
  });

  const isProjectPreview = computed(() => !!options?.from_project);

  return {
    course: course as Ref<CoursePageView>,
    otherSessions,
    nextSession,
    status,
    isRegistrationLink,
    isProjectPreview,
  };
};
